import { useMemo, useState } from 'react';
import { Form, Modal, Space, Tag, message } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Button } from '@components/ui/Button';
import { useHomeworks } from '@hooks/tenant/useHomeworks';
import { useCreateHomework } from '@hooks/tenant/useCreateHomework';
import { useUpdateHomework } from '@hooks/tenant/useUpdateHomework';
import { useDeleteHomework } from '@hooks/tenant/useDeleteHomework';
import { useTenantClasses } from '@hooks/tenant/useTenantClasses';
import { useTeachers } from '@hooks/tenant/useTeachers';

const EMPTY_TASK_DRAFT = { title: '', note: '', required: true };

export function useHomeworkDiaryManager({ tenantId, user }) {
    const userRole = String(user?.role || '').toLowerCase();
    const isTeacherUser = userRole === 'teacher';
    const isStaffUser = userRole === 'staff';

    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState('create');
    const [editingHomework, setEditingHomework] = useState(null);
    const [gradePickerValue, setGradePickerValue] = useState(undefined);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [editingTaskIndex, setEditingTaskIndex] = useState(null);
    const [taskDraft, setTaskDraft] = useState(EMPTY_TASK_DRAFT);
    const [form] = Form.useForm();

    const { rows, total, loading } = useHomeworks({ tenantId, page: currentPage, pageSize: currentPageSize });
    const { rows: tenantClasses } = useTenantClasses({ tenantId });
    const { rows: teacherRows } = useTeachers({ tenantId, page: 1, pageSize: 200 });
    const createHomeworkMutation = useCreateHomework({ tenantId });
    const updateHomeworkMutation = useUpdateHomework({ tenantId });
    const deleteHomeworkMutation = useDeleteHomework({ tenantId });

    const classOptions = useMemo(
        () =>
            (tenantClasses || []).map((c) => ({
                value: c.id,
                label: c.displayName || [c.grade, c.section].filter(Boolean).join(' - ') || c.name || c.id,
            })),
        [tenantClasses]
    );

    const classLabelById = useMemo(() => {
        const map = new Map();
        classOptions.forEach((opt) => map.set(opt.value, opt.label));
        return map;
    }, [classOptions]);

    const teacherOptions = useMemo(
        () =>
            (teacherRows || []).map((teacher) => ({
                value: teacher.id || teacher.authUid || teacher.uid,
                label:
                    teacher.displayName
                    || `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim()
                    || teacher.email
                    || 'Teacher',
            })),
        [teacherRows]
    );

    const teacherLabelById = useMemo(() => {
        const map = new Map();
        teacherOptions.forEach((opt) => map.set(opt.value, opt.label));
        return map;
    }, [teacherOptions]);

    const currentUserLabel = useMemo(
        () =>
            user?.displayName
            || user?.name
            || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
            || user?.email
            || 'Teacher',
        [user]
    );

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const title = String(r.title || '').toLowerCase();
            const description = String(r.description || '').toLowerCase();
            const todos = Array.isArray(r.todos) ? r.todos.map((t) => `${t?.title || ''} ${t?.note || ''}`).join(' ').toLowerCase() : '';
            return title.includes(q) || description.includes(q) || todos.includes(q);
        });
    }, [rows, searchText]);

    const selectedAudienceType = Form.useWatch('audienceType', form);
    const selectedClassIds = Form.useWatch('classIds', form) || [];
    const tasks = Form.useWatch('todos', form) || [];

    const taskDraftHasValues = useMemo(() => {
        return Boolean((taskDraft.title || '').trim() || (taskDraft.note || '').trim() || taskDraft.required === false);
    }, [taskDraft]);

    const taskRows = useMemo(() => {
        return (tasks || []).map((task, index) => ({
            key: `task-${index}`,
            index,
            title: task?.title || '—',
            note: task?.note || '—',
            required: !!task?.required,
        }));
    }, [tasks]);

    const selectedClassChips = useMemo(
        () =>
            selectedClassIds
                .map((id) => ({ id, label: classLabelById.get(id) || id }))
                .filter((chip) => !!chip.id),
        [classLabelById, selectedClassIds]
    );

    const selectableClassOptions = useMemo(
        () => classOptions.filter((option) => !selectedClassIds.includes(option.value)),
        [classOptions, selectedClassIds]
    );

    const resetTaskDraft = () => {
        setTaskDraft(EMPTY_TASK_DRAFT);
        setEditingTaskIndex(null);
    };

    const openTaskCreateModal = () => {
        setEditingTaskIndex(null);
        setTaskDraft(EMPTY_TASK_DRAFT);
        setTaskModalOpen(true);
    };

    const openTaskEditModal = (index) => {
        const existing = tasks?.[index];
        if (!existing) return;
        setEditingTaskIndex(index);
        setTaskDraft({
            title: existing.title || '',
            note: existing.note || '',
            required: typeof existing.required === 'boolean' ? existing.required : true,
        });
        setTaskModalOpen(true);
    };

    const closeTaskModal = () => {
        setTaskModalOpen(false);
        resetTaskDraft();
    };

    const saveTaskDraft = () => {
        const title = String(taskDraft.title || '').trim();
        if (!title) {
            message.warning('Task title is required');
            return;
        }
        const nextTasks = [...(form.getFieldValue('todos') || [])];
        const prepared = {
            title,
            note: String(taskDraft.note || '').trim() || null,
            required: !!taskDraft.required,
        };

        if (typeof editingTaskIndex === 'number') {
            nextTasks[editingTaskIndex] = prepared;
        } else {
            nextTasks.push(prepared);
        }
        form.setFieldValue('todos', nextTasks);
        closeTaskModal();
    };

    const removeTask = (index) => {
        const nextTasks = (form.getFieldValue('todos') || []).filter((_, i) => i !== index);
        form.setFieldValue('todos', nextTasks);
    };

    const removeSelectedClass = (classId) => {
        form.setFieldValue(
            'classIds',
            selectedClassIds.filter((id) => id !== classId)
        );
    };

    const addSelectedClass = (classId) => {
        if (!classId) return;
        const current = form.getFieldValue('classIds') || [];
        if (current.includes(classId)) {
            setGradePickerValue(undefined);
            return;
        }
        form.setFieldValue('classIds', [...current, classId]);
        setGradePickerValue(undefined);
    };

    const resetMainModal = () => {
        setModalOpen(false);
        setEditingHomework(null);
        setMode('create');
        form.resetFields();
        setGradePickerValue(undefined);
        closeTaskModal();
    };

    const openCreate = () => {
        setMode('create');
        setEditingHomework(null);
        form.resetFields();
        form.setFieldsValue({
            audienceType: 'all',
            status: true,
            assignedByTeacherId: undefined,
            todos: [],
        });
        setGradePickerValue(undefined);
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setMode('edit');
        setEditingHomework(row);
        form.resetFields();
        form.setFieldsValue({
            title: row?.title || '',
            description: row?.description || '',
            dueDate: row?.dueDate || '',
            audienceType: row?.audienceType || 'all',
            classIds: row?.classIds || [],
            status: (row?.status || 'active') === 'active',
            assignedByTeacherId: row?.assignedBy?.teacherId || undefined,
            todos: Array.isArray(row?.todos) && row.todos.length
                ? row.todos.map((todo) => ({ title: todo?.title || '', note: todo?.note || '', required: !!todo?.required }))
                : [],
        });
        setGradePickerValue(undefined);
        setModalOpen(true);
    };

    const confirmDelete = (row) => {
        Modal.confirm({
            title: 'Delete homework?',
            content: `This will delete "${row?.title || 'this homework'}".`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                await deleteHomeworkMutation.mutateAsync({ homeworkId: row.id });
                message.success('Homework deleted');
            },
        });
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        const selectedTeacherId = values.assignedByTeacherId;
        const selectedTeacherLabel = teacherLabelById.get(selectedTeacherId) || null;
        const selectedClassIdsForAudience = values.audienceType === 'grade' ? (form.getFieldValue('classIds') || []) : [];
        if (values.audienceType === 'grade' && selectedClassIdsForAudience.length === 0) {
            message.warning('Please select at least one class/grade');
            return;
        }
        const payload = {
            title: values.title,
            description: values.description || null,
            dueDate: values.dueDate || null,
            audienceType: values.audienceType,
            classIds: selectedClassIdsForAudience,
            status: mode === 'create' ? 'active' : (values.status ? 'active' : 'inactive'),
            assignedBy: isTeacherUser
                ? {
                    teacherId: user?.authUid || user?.uid || user?.id || null,
                    name: currentUserLabel,
                    role: 'teacher',
                    email: user?.email || null,
                }
                : selectedTeacherId
                    ? {
                        teacherId: selectedTeacherId,
                        name: selectedTeacherLabel,
                        role: 'teacher',
                        email: null,
                    }
                    : null,
            todos: (values.todos || []).map((todo) => ({
                title: todo.title,
                note: todo.note || null,
                required: !!todo.required,
            })),
        };

        try {
            if (mode === 'create') {
                await createHomeworkMutation.mutateAsync({
                    payload,
                    createdBy: user?.authUid || user?.uid || user?.email || null,
                });
                message.success('Homework diary created');
            } else {
                const homeworkId = editingHomework?.id;
                if (!homeworkId) throw new Error('Missing homework id');
                await updateHomeworkMutation.mutateAsync({ homeworkId, patch: payload });
                message.success('Homework diary updated');
            }
            resetMainModal();
        } catch (error) {
            message.error(error?.message || 'Failed to save homework diary');
        }
    };

    const homeworkColumns = useMemo(
        () => [
            { title: 'Homework', dataIndex: 'title', key: 'title', render: (v) => v || '—' },
            { title: 'Assigned For', key: 'audienceType', render: (_, r) => (r.audienceType === 'all' ? 'All Students' : 'Grade wise') },
            {
                title: 'Grades',
                key: 'classIds',
                render: (_, r) => {
                    if (r.audienceType === 'all') return 'All';
                    const ids = Array.isArray(r.classIds) ? r.classIds : [];
                    if (!ids.length) return '—';
                    return ids.map((id) => classLabelById.get(id) || id).join(', ');
                },
            },
            ...(isTeacherUser
                ? [{
                    title: 'Assigned By',
                    key: 'assignedBy',
                    render: (_, r) =>
                        r?.assignedBy?.name
                        || teacherLabelById.get(r?.assignedBy?.teacherId)
                        || r?.assignedBy?.email
                        || r?.createdBy
                        || '—',
                }]
                : []),
            { title: 'Due Date', dataIndex: 'dueDate', key: 'dueDate', render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—') },
            {
                title: 'Todos',
                key: 'todos',
                render: (_, r) => {
                    const count = Array.isArray(r.todos) ? r.todos.length : 0;
                    return <Tag color="blue">{count} Todo{count === 1 ? '' : 's'}</Tag>;
                },
            },
            {
                title: 'Status',
                key: 'status',
                render: (_, r) => <Tag color={(r.status || 'active') === 'active' ? 'green' : 'default'}>{(r.status || 'active').toUpperCase()}</Tag>,
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, row) => (
                    <Space onClick={(e) => e.stopPropagation()}>
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
                            Edit
                        </Button>
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            loading={deleteHomeworkMutation.isPending}
                            onClick={() => confirmDelete(row)}
                        >
                            Delete
                        </Button>
                    </Space>
                ),
            },
        ],
        [classLabelById, confirmDelete, deleteHomeworkMutation.isPending, isTeacherUser, openEdit, teacherLabelById]
    );

    const taskColumns = useMemo(() => [
        { title: 'Task', dataIndex: 'title', key: 'title' },
        { title: 'Details', dataIndex: 'note', key: 'note' },
        { title: 'Required', dataIndex: 'required', key: 'required', render: (value) => <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag> },
        {
            title: 'Action',
            key: 'action',
            render: (_, row) => (
                <Space onClick={(e) => e.stopPropagation()}>
                    <Button type="text" icon={<EditOutlined />} onClick={() => openTaskEditModal(row.index)}>
                        Edit
                    </Button>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeTask(row.index)}>
                        Delete
                    </Button>
                </Space>
            ),
        },
    ], [openTaskEditModal, removeTask]);

    return {
        form,
        mode,
        modalOpen,
        taskModalOpen,
        editingTaskIndex,
        taskDraft,
        taskDraftHasValues,
        gradePickerValue,
        selectedAudienceType,
        isTeacherUser,
        isStaffUser,
        classOptions,
        selectableClassOptions,
        selectedClassChips,
        teacherOptions,
        homeworkColumns,
        taskColumns,
        filteredRows,
        loading,
        total,
        searchText,
        currentPage,
        currentPageSize,
        taskRows,
        createHomeworkMutation,
        updateHomeworkMutation,
        deleteHomeworkMutation,
        setCurrentPage,
        setCurrentPageSize,
        setSearchText,
        setTaskDraft,
        setGradePickerValue,
        openCreate,
        openEdit,
        confirmDelete,
        resetMainModal,
        handleSubmit,
        openTaskCreateModal,
        openTaskEditModal,
        addSelectedClass,
        removeSelectedClass,
        removeTask,
        closeTaskModal,
        saveTaskDraft,
        resetTaskDraft,
    };
}
